import React, { useMemo } from 'react'
import Gap from './Gap'
import { ImportGroup, ImportedPlaceSummary } from '../api/bookmark'

export interface ImportGroupPickerProps {
  /** 링크 이름 (예: "맛동여지도 : 수원·용인·동탄·오산") */
  title: string | null
  groups: ImportGroup[]
  /** 가져온 장소 전부. 묶음 안을 들여다볼 때 이름·주소를 여기서 찾는다. */
  places: ImportedPlaceSummary[]
  /** 고른 장소들. 묶음이 아니라 **장소 단위**로 센다. */
  selectedPlaceIds: number[]
  onTogglePlace: (id: number) => void
  /** 묶음 하나를 통째로 켜고 끈다 (묶음 목록의 체크박스) */
  onToggleGroup: (groupId: string) => void
  /** 전부 켜고 끈다. 묶음 안에서는 그 묶음만. */
  onToggleAll: (groupId?: string) => void
  /**
   * 지금 들여다보고 있는 묶음. null 이면 묶음 목록을 보여준다.
   *
   * 부모가 들고 있는 이유 — 이게 화면의 한 '단계' 라서다. 여기서만 들고
   * 있으면 뒤로가기(스와이프·기기 버튼 포함)가 이 단계를 모르고 화면을
   * 통째로 떠난다.
   */
  openedGroupId: string | null
  onOpenGroup: (id: string | null) => void
  /**
   * 묶음 종류. 'list' 면 네이버 공개 리스트(사람 단위)라 제목과 안내 문구가
   * 바뀐다. 안 주면 지역 묶음.
   */
  groupKind?: 'region' | 'list'
  /** 공개 리스트 주인 닉네임 — 제목 '○○님의 공개 리스트' */
  ownerName?: string | null
}

/**
 * 링크 안의 장소를 고르는 화면.
 *
 * 왜 묶어서 보여주나
 * -----------------
 * 네이버 기획전 하나에 489곳이 들어 있다. 한 줄로 늘어놓으면 고를 수가 없어
 * 서버가 주소로 묶어 주고, 화면은 묶음 → 그 안의 상호 순으로 보여준다.
 *
 * 고르는 단위는 **상호 하나**다
 * ---------------------------
 * 묶음 체크는 그 안의 상호를 한꺼번에 켜고 끄는 손잡이일 뿐이고, 실제로
 * 담기는 것은 켜진 상호들이다. 그래서 '수원시 영통구에서 세 곳만' 같은
 * 고르기가 된다.
 *
 * 두 모습
 *   묶음 목록   ☐ 수원시 영통구 (106곳)  >
 *   묶음 안     ☐ 상호 하나하나 + 전체 선택
 *
 * 펼쳐서 덧붙이지 않고 화면을 바꾼다 — 한 묶음이 100곳을 넘어, 펼치면
 * 아래 묶음들이 저 멀리 밀려난다.
 *
 * 생김새는 '링크로 가져온 뒤' 화면을 따른다 — 체크박스 없는 글자 버튼과
 * bg-472410-5% 박스 (OnboardingImportRank 의 체크리스트와 같다).
 */
const ImportGroupPicker: React.FC<ImportGroupPickerProps> = ({
  title,
  groups,
  places,
  selectedPlaceIds,
  onTogglePlace,
  onToggleGroup,
  onToggleAll,
  openedGroupId,
  onOpenGroup,
  groupKind = 'region',
  ownerName,
}) => {
  /*
   * 링크 전체의 장소. 한 곳이 여러 리스트에 들어 있을 수 있어(공개 리스트)
   * 묶음별 count 를 더하면 겹친 만큼 많아진다 — 그러면 다 골라도 '전체 해제'
   * 로 안 바뀐다. 겹친 것을 한 번만 센다.
   */
  const allIds = useMemo(
    () => Array.from(new Set(groups.flatMap((g) => g.place_ids))),
    [groups]
  )

  const isList = groupKind === 'list'
  const heading = isList
    ? ownerName
      ? `${ownerName}님의 공개 리스트`
      : '공개 리스트'
    : title

  /*
   * 장소를 id 로 찾는 표와 고른 것의 집합.
   *
   * useMemo 가 없으면 체크할 때마다 수백 곳을 훑는다. 466곳짜리 기획전에서
   * 화면이 멈추는 원인이었다.
   */
  const byId = useMemo(() => new Map(places.map((p) => [p.id, p])), [places])
  const picked = useMemo(() => new Set(selectedPlaceIds), [selectedPlaceIds])

  const opened = openedGroupId
    ? groups.find((g) => g.id === openedGroupId) ?? null
    : null

  const checkbox = (on: boolean) => (
    <img
      src={
        on
          ? '/images/checkbox-bold-filled.png'
          : '/images/checkbox-bold-unchecked.png'
      }
      alt=""
      className="flex-shrink-0 w-22px h-22px object-contain"
    />
  )

  // ── 묶음 안: 상호를 하나씩 고른다 ───────────────────────
  if (opened) {
    const allPicked =
      opened.place_ids.length > 0 && opened.place_ids.every((id) => picked.has(id))

    return (
      <div className="w-full flex flex-col">
        {/* 개수는 안 적는다 — 아래에 상호가 그대로 늘어서 있어 셀 필요가 없다 */}
        <span className="text-style-labelL text-textColorText2">
          {opened.name}
        </span>

        <Gap height={16} />

        <button
          type="button"
          onClick={() => onToggleAll(opened.id)}
          className="self-start mb-8px text-14px text-4B3621-80% font-bold"
        >
          {allPicked ? '전체 해제' : '전체 선택'}
        </button>

        <div className="flex flex-col gap-8px">
          {opened.place_ids.map((id) => {
            const place = byId.get(id)
            if (!place) return null
            return (
              <button
                key={id}
                type="button"
                onClick={() => onTogglePlace(id)}
                className="w-full px-12px py-12px rounded-12px bg-472410-5% flex items-center gap-12px text-left hover:opacity-70"
              >
                {checkbox(picked.has(id))}
                <span className="flex-1 min-w-0">
                  <span className="block text-style-button2 text-brown-80%-main text-ellipsis-1">
                    {place.name}
                  </span>
                  {place.address && (
                    <span className="block text-13px text-textColorText3 text-ellipsis-1">
                      {place.address}
                    </span>
                  )}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  // ── 묶음 목록 ────────────────────────────────────────────
  const allPicked = allIds.length > 0 && allIds.every((id) => picked.has(id))

  return (
    <div className="w-full flex flex-col">
      {heading && (
        <>
          <span className="text-style-labelL text-textColorText2 text-ellipsis-2">
            {heading}
          </span>
          <Gap height={4} />
        </>
      )}

      <span className="text-style-captionM text-textColorText3">
        {isList ? '가져올 리스트를 골라주세요' : '가져올 지역을 골라주세요'}
      </span>

      <Gap height={16} />

      <button
        type="button"
        onClick={() => onToggleAll()}
        className="self-start mb-8px text-14px text-4B3621-80% font-bold"
      >
        {allPicked ? '전체 해제' : '전체 선택'}
      </button>

      <div className="flex flex-col gap-8px">
        {groups.map((group) => {
          /*
           * 묶음 체크는 '그 안이 전부 켜졌나' 를 보여준다. 일부만 켜졌을 때는
           * 꺼진 것으로 그린다 — 반쯤 켜진 표시를 따로 만들면 무슨 뜻인지
           * 설명해야 하는데, 들어가 보면 어느 것이 켜졌는지 바로 보인다.
           */
          const groupPicked =
            group.place_ids.length > 0 &&
            group.place_ids.every((id) => picked.has(id))

          return (
            <div
              key={group.id}
              className="w-full rounded-12px bg-472410-5% flex items-center gap-12px px-12px py-12px"
            >
              <button
                type="button"
                onClick={() => onToggleGroup(group.id)}
                className="flex-1 min-w-0 flex items-center gap-12px text-left hover:opacity-70"
              >
                {checkbox(groupPicked)}
                {/*
                  상호 카드와 같은 모양으로 둔다 — 윗줄은 이름, 아랫줄은
                  연한 글씨. 두 화면을 오갈 때 카드 크기가 달라지면 다른
                  화면처럼 느껴진다.
                */}
                <span className="flex-1 min-w-0">
                  <span className="block text-style-button2 text-brown-80%-main text-ellipsis-1">
                    {group.name}
                  </span>
                  <span className="block text-13px text-textColorText3 text-ellipsis-1">
                    {group.count}곳
                  </span>
                </span>
              </button>

              {/*
                들여다보기. 고르기와 따로 둔다 — 안을 보려고 눌렀는데
                선택까지 바뀌면 곤란하다.
              */}
              <button
                type="button"
                onClick={() => onOpenGroup(group.id)}
                aria-label={`${group.name} 장소 보기`}
                className="flex-shrink-0 w-24px h-24px flex items-center justify-center text-brown-80%-main hover:opacity-70"
              >
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                  <path
                    d="M1 1L7 7L1 13"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ImportGroupPicker
